/**
 * Helpers that recover from "old HTML pointing at missing hashed assets"
 * after a fresh deploy. The inline script in `index.html` handles the very
 * first paint; these helpers cover runtime failures that surface later
 * (e.g. dynamic `import()` for a code-split chunk, or a React render error
 * whose root cause is a missing module).
 */

const RELOAD_KEY = "__tih_stale_reload__";
const MAX_RELOADS = 1;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message =
    (error instanceof Error ? error.message : String(error)) || "";
  const name = error instanceof Error ? error.name : "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [^\s]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Some browsers surface a generic "NetworkError" for <link>/<script> 404s.
    (/NetworkError/i.test(message) && /module|chunk|asset/i.test(message))
  );
}

function getReloadCount(): number {
  try {
    return parseInt(sessionStorage.getItem(RELOAD_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function bumpReloadCount() {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(getReloadCount() + 1));
  } catch {
    /* storage disabled — best effort */
  }
}

export function clearReloadCount() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Performs a one-shot hard reload with a cache-busting query param so that
 * the browser re-validates `index.html` against origin. Returns `true` if a
 * reload was triggered, `false` if we're already at the retry cap (in which
 * case the caller should render a human-friendly fallback instead).
 */
export function hardReloadForStaleAssets(): boolean {
  if (getReloadCount() >= MAX_RELOADS) return false;
  bumpReloadCount();
  const url = new URL(window.location.href);
  url.searchParams.set("__r", Date.now().toString(36));
  window.location.replace(url.toString());
  return true;
}

/**
 * Unregister any previously-installed service workers and clear their caches.
 * We don't ship a service worker today, but earlier experiments (or future
 * ones) could leave one installed on a returning visitor's device where it
 * would happily serve a stale `index.html` forever. This makes sure that
 * never silently breaks the site.
 */
export async function purgeStaleServiceWorkers(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    /* non-fatal */
  }
}
